import uniqueId from 'lodash-es/uniqueId';
import * as React from 'react';
import { DetailedHTMLProps, HTMLAttributes, useEffect, useState } from 'react';
import { useCommonInputStyles } from '@tcl-boron-foundations/input';
import { BaseInputProps, DropdownOption } from '@tcl-argon-models/models';
import { mergeClasses } from '@tcl-argon-utils/utils';
import { useCheckboxStyles, useMultiSelectDropdownStyles } from './styles';
import { useHierarchicalStyles } from './hierarchicalStyles';
import { StatusVariant } from '@tcl-argon-utils/utils';
import { useScrollbarStyles } from '@tcl-boron-styles/hooks';

import { DropdownCore } from './DropdownCore';
import { ComboBoxCore } from './ComboBoxCore';
import { TypeaheadCore } from './TypeaheadCore';
import { HierarchicalCore } from './HierarchicalCore';
import {
  getHierarchicalDataForList,
  propagateDown,
  propagateSelectionDown,
  propagateSelectionsUp,
  propagateUp,
  setDefaultData,
} from './coreUtils';

type CheckboxStatus = 'selected' | 'indeterminate' | 'unselected';
export interface HierarchicalDropdownOption extends DropdownOption {
  parentId: string;
  linkLabel?: string;
}

export interface IHierarchicalDropdownOption extends HierarchicalDropdownOption {
  hasParent: boolean;
  isParentLink?: boolean;
  children?: IHierarchicalDropdownOption[];
  isExpanded?: boolean;
  status?: CheckboxStatus;
  childValue?: string;
  parentItem: HierarchicalDropdownOption;
  level?: number;
}

type DivElement = DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export interface SelectProps extends BaseInputProps, Omit<DivElement, 'onChange'> {
  /** If `true`, show an "X" button that, when clicked, will clear a user's current selection. */
  clearable?: boolean;
  /**
   * A handler called whenever this input changes.
   * All selected options are passed in an array as the only argument to the callback.
   */
  onChange: (selectedItems: DropdownOption[]) => void;
  /** An array of DropdownOptions that use user can select. */
  options: DropdownOption[];
  /** An array of DropdownOptions that represent a user's current selections. */
  value?: DropdownOption[];
  /** whether the dropdown renders as primary or secondary style, the main difference being border radius */
  dropdownType?: 'primary' | 'secondary';
  /** status either 'success' | 'error' | 'warning' | 'default' changes colors to visually reflect status of dropdown */
  status?: StatusVariant;
  isSingleSelect: boolean;
  isComboBox: boolean;
  isLoading?: boolean;
  isTypeahead: boolean;
  inputValue?: string;
  onInputValueChange?: (inputValue: string) => void;
  onDebounceChange?: (value: string) => void;
  debounceTimeOverride?: number;
  selectedItemsTextOverride?: string;
  menuWidth?: string;
  /** when true, fixed strategy is used in Popper for menu placement */
  escapeClippingArea: boolean;
  defaultOptions?: DropdownOption[];
  defaultOptionsLabel?: string;
  filterOption?: (inputValue: string, option: DropdownOption) => boolean;
}

export interface HierarchicalSelectProps
  extends SelectProps,
    Omit<SelectProps, 'onChange' | 'options' | 'value' | 'defaultOptions' | 'filterOption' | 'clearable'> {
  /**
   * A handler called whenever this input changes.
   * All selected options are passed in an array as the only argument to the callback.
   */
  onChange: (selectedItems: HierarchicalDropdownOption[]) => void;
  /** An array of HierarchicalDropdownOptions that use user can select. */
  options: HierarchicalDropdownOption[];
  /** An array of HierarchicalDropdownOptions that represent a user's current selections. */
  value?: HierarchicalDropdownOption[];
  defaultOptions?: HierarchicalDropdownOption[];
  searchResults?: HierarchicalDropdownOption[];
}

export const HierarchicalSelect: React.FC<HierarchicalSelectProps> = ({
  classes: classOverrides = {},
  className = '',
  isComboBox,
  disabled = false,
  hideLabel = false,
  label,
  onChange,
  options: hierarchicalData,
  placeholder = 'Select',
  subContent,
  value: selectedItems = [],
  id,
  status = 'default',
  dropdownType = 'primary',
  isSingleSelect = false,
  isLoading = false,
  isTypeahead = false,
  inputValue = '',
  onInputValueChange = () => {},
  onDebounceChange = () => {},
  debounceTimeOverride = 500,
  defaultOptions,
  defaultOptionsLabel,
  searchResults,
  ...props
}) => {
  [id] = useState(id || uniqueId('multi_select_hierarchical_typeahead'));
  const tabIndex = isComboBox || isTypeahead || disabled ? -1 : 0;
  const classes = mergeClasses(
    useMultiSelectDropdownStyles({
      status,
      isDisabled: disabled,
      isPrimary: dropdownType === 'primary',
      isEmpty: selectedItems.length > 0,
      isLoading,
    }),
    useScrollbarStyles(),
    useCheckboxStyles(),
    useCommonInputStyles({ subContent, status }),
    useHierarchicalStyles({
      status,
      isDisabled: disabled,
      isPrimary: dropdownType === 'primary',
      isEmpty: selectedItems.length > 0,
      isLoading,
    }),
    classOverrides,
    { root: className },
  );
  const shouldShowClearbutton = inputValue !== '' && inputValue !== null && inputValue !== undefined;

  const checkCheckboxState = (
    selectedOptions: IHierarchicalDropdownOption[],
    flattenedStructure: IHierarchicalDropdownOption[],
  ): { updatedStructure: IHierarchicalDropdownOption[]; updatedSelectedOptions: IHierarchicalDropdownOption[] } => {
    let newSelectedOptions: IHierarchicalDropdownOption[] = selectedOptions;
    let updatedStructure: IHierarchicalDropdownOption[] = flattenedStructure;
    selectedOptions.forEach((checkbox) => {
      const propogateDownOptions = propagateSelectionDown(checkbox.value, true, selectedOptions, flattenedStructure);
      const propogateDownStructure = propagateDown(checkbox.value, propogateDownOptions, flattenedStructure);
      const optionsAfterPropogation = propagateSelectionsUp(
        true,
        checkbox.value,
        propogateDownOptions,
        flattenedStructure,
      );
      newSelectedOptions = [...new Set([...optionsAfterPropogation])];
      updatedStructure = propagateUp(true, checkbox.value, propogateDownStructure);
      onChange(newSelectedOptions);
    });
    return { updatedSelectedOptions: newSelectedOptions, updatedStructure: updatedStructure };
  };

  const fullHierarchicalData = setDefaultData(hierarchicalData);
  const fullSelectedItems = getHierarchicalDataForList(selectedItems, hierarchicalData);
  const fullSearchResults = getHierarchicalDataForList(searchResults, hierarchicalData);
  const fullDefaultOptions = getHierarchicalDataForList(defaultOptions, hierarchicalData);

  useEffect(() => {
    if (!isSingleSelect) {
      const updatedState = checkCheckboxState(fullSelectedItems, fullHierarchicalData);
      if (updatedState && updatedState.updatedSelectedOptions && updatedState.updatedStructure) {
        onChange([...new Set(updatedState.updatedSelectedOptions)]);
      }
    }
  }, []);

  return (
    <HierarchicalCore
      {...{
        isSingleSelect,
        hierarchicalData: fullHierarchicalData,
        onChange,
        id,
        classes,
        label,
        hideLabel,
        disabled,
        tabIndex,
        selectedItems: fullSelectedItems,
        placeholder,
        subContent,
        isLoading,
        inputValue,
        onInputValueChange,
        onDebounceChange,
        debounceTimeOverride,
        shouldShowClearbutton,
        defaultOptions: fullDefaultOptions,
        defaultOptionsLabel,
        searchResults: fullSearchResults,
        ...props,
      }}
    />
  );
};

export const Select: React.FC<SelectProps> = ({
  classes: classOverrides = {},
  className = '',
  isComboBox,
  clearable = isComboBox ? true : false,
  disabled = false,
  hideLabel = false,
  label,
  onChange,
  options: items,
  placeholder = 'Select',
  subContent,
  value: selectedItems = [],
  id,
  status = 'default',
  dropdownType = 'primary',
  isSingleSelect = false,
  isLoading = false,
  isTypeahead = false,
  inputValue = '',
  onInputValueChange = () => {},
  onDebounceChange = () => {},
  debounceTimeOverride = 500,
  defaultOptions,
  defaultOptionsLabel,
  filterOption,
  ...props
}) => {
  [id] = useState(id || uniqueId('multi_select_dropdown_'));

  const tabIndex = isComboBox || isTypeahead || disabled ? -1 : 0;
  const classes = mergeClasses(
    useMultiSelectDropdownStyles({
      status,
      isDisabled: disabled,
      isPrimary: dropdownType === 'primary',
      isEmpty: selectedItems.length > 0,
      isLoading,
    }),
    useScrollbarStyles(),
    useCheckboxStyles(),
    useCommonInputStyles({ subContent, status }),
    useHierarchicalStyles({
      status,
      isDisabled: disabled,
      isPrimary: dropdownType === 'primary',
      isEmpty: selectedItems.length > 0,
      isLoading,
    }),
    classOverrides,
    { root: className },
  );
  const shouldShowClearbutton = inputValue !== '' && inputValue !== null && inputValue !== undefined;
  if (isComboBox && isTypeahead) {
    return (
      <TypeaheadCore
        {...{
          isSingleSelect,
          items,
          onChange,
          id,
          classes,
          label,
          hideLabel,
          disabled,
          tabIndex,
          selectedItems,
          placeholder,
          clearable,
          subContent,
          isLoading,
          inputValue,
          onInputValueChange,
          onDebounceChange,
          debounceTimeOverride,
          shouldShowClearbutton,
          defaultOptions,
          defaultOptionsLabel,
          ...props,
        }}
      />
    );
  } else if (isComboBox && !isTypeahead) {
    return (
      <ComboBoxCore
        {...{
          isSingleSelect,
          items,
          onChange,
          id,
          classes,
          label,
          hideLabel,
          disabled,
          tabIndex,
          selectedItems,
          placeholder,
          clearable,
          subContent,
          filterOption,
          ...props,
        }}
      />
    );
  } else {
    return (
      <DropdownCore
        {...{
          isSingleSelect,
          items,
          onChange,
          id,
          classes,
          label,
          hideLabel,
          disabled,
          tabIndex,
          selectedItems,
          placeholder,
          clearable,
          subContent,
          shouldShowClearbutton,
          ...props,
        }}
      />
    );
  }
};
